import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { cloneDeep } from 'lodash';
import Domain from '@/model/entity/billing/domain';
import DomainDto from '@/model/entity/billing/domain-dto';
import FormFieldInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-interface';
import FormFieldSetInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-set-interface';
import FormInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-interface';

@Component
export default class DomainForm extends Vue
{
    @Prop()
    private domain!: Domain;

    @Prop({
        type: Boolean,
        default: false,
    })
    private saving!: boolean;

    private form: FormInterface|null = null;

    private created()
    {
        this.generateForm();
    }

    @Watch('domain')
    private onDomainChanged()
    {
        this.generateForm();
    }

    /**
     * Handles form saves
     *
     * @param form
     */

    private async onSave(form: FormInterface)
    {
        if (!this.domain)
        {
            return;
        }

        // Generate a domain from the form data
        const domain = this.generateDomain(form);

        // Emit a save event with the domain
        this.$emit('save', domain);
    }

    /**
     * Generates the form from the domain
     */

    private generateForm()
    {
        if (!this.domain)
        {
            this.form = null;
            return;
        }

        const form: FormInterface =
        {
            items: [
                {
                    title: this.$t('general.field.basic_details').toString(),
                    fields: [
                        {
                            key: 'client',
                            type: 'integer',
                            title: this.$t('general.field.client').toString(),
                            value: this.domain.clientId,
                            required: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.clientService.clientFormOptionsCallback(search),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.clientService.clientFormTitlesCallback(values),
                            disableInitialOptionsCallback: true,
                            disableEmptySearchResults: true,
                        },
                        {
                            key: 'registrar',
                            type: 'string',
                            title: this.$t('domain.field.registrar').toString(),
                            value: this.domain.registrar,
                            required: true,
                            options: [
                                {
                                    value: '123 Reg',
                                    title: '123 Reg',
                                },
                                {
                                    value: 'Fasthosts',
                                    title: 'Fasthosts',
                                },
                                {
                                    value: 'GoDaddy',
                                    title: 'GoDaddy',
                                },
                                {
                                    value: 'ICO3',
                                    title: 'ICO3',
                                },
                                {
                                    value: 'ICO3 Joker',
                                    title: 'ICO3 Joker',
                                },
                                {
                                    value: 'ICO3 Namecheap',
                                    title: 'ICO3 Namecheap',
                                },
                                {
                                    value: 'ICO3 Nominet',
                                    title: 'ICO3 Nominet',
                                },
                                {
                                    value: 'ICO3 Pacnames',
                                    title: 'ICO3 Pacnames',
                                },
                                {
                                    value: 'ICO3 Total Registrations',
                                    title: 'ICO3 Total Registrations',
                                },
                                {
                                    value: 'Ja.net',
                                    title: 'Ja.net',
                                },
                                {
                                    value: 'Joker-DG',
                                    title: 'Joker-DG',
                                },
                                {
                                    value: 'Joker-NH',
                                    title: 'Joker-NH',
                                },
                                {
                                    value: 'Pac Names',
                                    title: 'Pac Names',
                                },
                                {
                                    value: 'Real Fusion Fasthosts',
                                    title: 'Real Fusion Fasthosts',
                                },
                                {
                                    value: 'Total Registrations',
                                    title: 'Total Registrations',
                                },
                                {
                                    value: 'TotalNic',
                                    title: 'TotalNic',
                                },
                            ],
                            dropdown: true,
                        },
                        {
                            key: 'stage',
                            type: 'integer',
                            title: this.$t('general.field.stage').toString(),
                            value: this.domain.stageId,
                            required: true,
                            optionsCallback: async(search: string) => this.$serviceContainer.domainService.domainStageFormOptionsCallback(search),
                            titlesCallback: async(values: string[]) => this.$serviceContainer.domainService.domainStageFormTitlesCallback(values),
                            dropdown: true,
                        },
                        {
                            key: 'dateExpiry',
                            type: 'date',
                            title: this.$t('general.field.date_expiry').toString(),
                            value: this.domain.dateExpiry,
                            required: false,
                        },
                        {
                            key: 'name',
                            type: 'string',
                            title: this.$t('general.field.name').toString(),
                            value: this.domain.name,
                            required: true,
                        },
                        {
                            key: 'comments',
                            type: 'string',
                            title: this.$t('general.field.comments').toString(),
                            value: this.domain.comments,
                            required: false,
                            multiline: true,
                            rich: true,
                            markdown: true,
                        },
                        {
                            key: 'managed',
                            type: 'boolean',
                            title: this.$t('domain.field.managed').toString(),
                            value: this.domain.managed,
                            required: true,
                        },
                        {
                            key: 'disabled',
                            type: 'boolean',
                            title: this.$t('general.field.disabled').toString(),
                            value: this.domain.disabled,
                            required: true,
                        },
                    ],
                    required: true,
                },
            ],
            stepper: true,
        };

        this.form = Object.assign({}, form);
    }

    /**
     * Generates the domain from the form
     */

    private generateDomain(form: FormInterface): Domain
    {
        if (!this.domain)
        {
            return new Domain(new DomainDto());
        }

        const domain = new Domain(cloneDeep(this.domain.dto));

        this.addFieldValues(domain, form.items);

        return domain;
    }

    /**
     * Adds field values from form items to the passed domain
     *
     * @param domain
     * @param items
     */

    private addFieldValues(domain: Domain, items: (FormFieldSetInterface | FormFieldInterface)[])
    {
        for (const item of items)
        {
            if (!('key' in item))
            {
                this.addFieldValues(domain, item.fields);
                continue;
            }

            switch (item.key)
            {
            case 'client':
                domain.clientId = item.value as number;
                break;

            case 'registrar':
                domain.registrar = item.value as string;
                break;

            case 'stage':
                domain.stageId = item.value as number;
                break;

            case 'dateExpiry':
                domain.dateExpiry = item.value as Date;
                break;

            case 'name':
                domain.name = item.value as string;
                break;

            case 'comments':
                domain.comments = item.value as string;
                break;

            case 'managed':
                domain.managed = item.value as boolean;
                break;

            case 'disabled':
                domain.disabled = item.value as boolean;
                break;
            }
        }
    }
}